import styled from "@emotion/styled";
import { Stack, Typography } from "@mui/material";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import { StyledFormLabelWrapper } from "Forms/Components/FormStyles";
import { type FieldErrorProps, useFieldError } from "Forms/Hooks/useFieldError";
import { Spacing } from "Infrastructure/Themes/Themes";
import { isNoU } from "Infrastructure/Utils/ObjectUtils";
import { isPropValid } from "Infrastructure/Utils/StyleUtils";
import * as React from "react";
import type { JSX } from "react";
import { type IMaskInputProps, IMaskMixin } from "react-imask";
import { Flex } from "../Core/Flex";

export type MaskType = IMaskInputProps<any>["mask"];
export type MaskBlockType = Record<
  string,
  {
    mask: MaskType;
    from?: number;
    to?: number;
    lazy?: boolean;
  }
>;

type Props = TextFieldProps &
  FieldErrorProps & {
    label?: string;
    characterCounter?: React.ReactNode;
    name?: string;
    mask?: MaskType;
    unmask?: boolean | "typed";
    max?: number;
    min?: number;
    onMaskInputChange?: (val: string) => void;
    onInputChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
    inputEndAdornment?: JSX.Element;
    inputStartAdornment?: JSX.Element;
    labelEndAdornment?: JSX.Element;
    maxLength?: number;
    scale?: number;
    unit?: string;
    hasFilterStyle?: boolean;
    maskBlocks?: MaskBlockType;
    thousandsSeparator?: string;
    accept?: string;
  };

const StyledInput = styled(TextField, { shouldForwardProp: isPropValid })<{
  $hasFilterStyle: boolean;
  $isEmpty: boolean;
  $hasError: boolean;
}>`

  .MuiInputBase-formControl {
    background-color: ${(props) => props.theme.palette.common.white};
  }

  .MuiInputBase-root {
      ${(props) =>
        props.$hasError &&
        `
          background-color: ${props.theme.palette.error.light} !important;
      `}
  }

  .MuiInputBase-input {
    box-sizing: border-box;
    height: 40px;
    padding-top: ${(props) => props.theme.spacing(props.$hasFilterStyle ? 1 : 1.2)};
    padding-bottom: ${(props) => props.theme.spacing(props.$hasFilterStyle ? 1 : 1.2)};
    color: ${(props) => props.theme.palette.text.primary};
    font-size: 16px;
    font-weight: 400;
    line-height: 1.4;

    &:-webkit-autofill,
    &:-webkit-autofill:hover &:-webkit-autofill:focus {
      box-shadow: 0 0 0px 1000px
        ${(props) => props.theme.palette.background.default} inset;
      color: ${(props) => props.theme.palette.text.primary};
    }
    &::placeholder {
      color: ${(props) => props.theme.colors.placeholder};
      font-weight: 400;
      opacity: 1;
    }

    &[type="file"]::file-selector-button {
      display: none;
    }

    ${(props) =>
      props.$isEmpty &&
      `
      &[type="file"] {
        &:after {
          content: "${props.placeholder ?? ""}";
          position: absolute;
          left: 2px;
          top: 2px;
          bottom: 2px;
          right: 2px;
          background-color: ${props.theme.colors.fieldBackground};
          color: ${props.theme.colors.placeholder};
          font-weight: 400;
          display: flex;
          align-items: center;
          padding-left: ${props.theme.spacing(1.2)};
        }
      } 

    `}
  }

  .MuiInputLabel-root {
    font-size: 18px;
  }

  .MuiOutlinedInput-notchedOutline {
    border: 1px solid
      ${(props) => (props.$hasFilterStyle ? props.theme.palette.primary.main : props.theme.colors.border)};
    border-radius: ${(props) => props.theme.spacing(Spacing.M)};
  }

  .MuiOutlinedInput-multiline {
    padding: 0;
  }

  .MuiFormHelperText-root {
    margin: 0;
    min-height: ${(props) => (props.$hasFilterStyle ? 0 : "20px")};
  }

  .Mui-disabled {
    &.MuiInputBase-formControl {
      background-color: ${(props) => props.theme.colors.primaryDisabled};

      .MuiInputBase-input {
        color: ${(props) => props.theme.colors.textDisabled};
      }

      .MuiOutlinedInput-notchedOutline {
        border-color: ${(props) => props.theme.colors.border};
      }
    }
  }

  &:hover {
    .MuiOutlinedInput-notchedOutline {
      transition: border-color 200ms;
      border-color: ${(props) => props.theme.colors.textSecondary};
    }
  }
`;

const StyledInputWrapper = styled.div`
  display: flex;
  flex-flow: column;

  .input-wrapper {
    position: relative;
  }
`;

const StyledUnit = styled.div`
  position: absolute;
  pointer-events: none;
  top: ${(props) => props.theme.spacing(0.5)};
  bottom: 20px;
  right: 15px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
`;

const StyledInputStartAdornment = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${(props) => props.theme.spacing(1)};
  pointer-events: none;
`;

const MaskedInput = IMaskMixin(({ inputRef, ...props }: any) => {
  return <StyledInput {...props} inputRef={inputRef} />;
}) as any;

export const TextInput: React.FunctionComponent<Props> = (props) => {
  const { hasError, errorMessage, originalProps } = useFieldError(props);
  const {
    label,
    characterCounter,
    name,
    mask,
    unmask,
    max,
    min,
    error,
    onMaskInputChange,
    onInputChange,
    value,
    inputEndAdornment,
    inputStartAdornment,
    labelEndAdornment,
    maxLength,
    scale,
    unit,
    hasFilterStyle,
    maskBlocks,
    thousandsSeparator,
    accept,
    slotProps: callerSlotProps,
    ...rest
  } = originalProps;

  const handleMaskInputChange = (nextValue: string) => {
    onMaskInputChange?.(nextValue);
  };

  const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  return (
    <StyledInputWrapper>
      <StyledFormLabelWrapper htmlFor={name} $disabled={rest.disabled ?? false}>
        {!!label && (
          <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"} mb={Spacing.S}>
            <Stack direction={"row"} gap={1}>
              <Typography fontWeight={500} variant="body2">
                {label}
              </Typography>
              {labelEndAdornment}
            </Stack>
            {characterCounter && characterCounter}
          </Flex>
        )}
      </StyledFormLabelWrapper>
      <div className={"input-wrapper"}>
        {isNoU(mask) ? (
          <StyledInput
            name={name}
            id={name}
            variant={"outlined"}
            fullWidth
            error={hasError || error}
            value={value}
            helperText={errorMessage}
            inputRef={inputRef}
            slotProps={{
              ...callerSlotProps,
              input: {
                endAdornment: inputEndAdornment,
                startAdornment: !!inputStartAdornment ? (
                  <StyledInputStartAdornment>{inputStartAdornment}</StyledInputStartAdornment>
                ) : undefined,
                ...callerSlotProps?.input,
              },
              htmlInput: {
                maxLength: maxLength,
                accept: accept,
                ...callerSlotProps?.htmlInput,
              },
            }}
            $hasFilterStyle={hasFilterStyle ?? false}
            $isEmpty={!inputRef.current?.value}
            $hasError={!!error}
            {...rest}
            onChange={onInputChange}
          />
        ) : (
          <MaskedInput
            className="masked"
            mask={mask}
            unmask={unmask ?? true}
            blocks={maskBlocks}
            radix=","
            mapToRadix={["."]}
            thousandsSeparator={thousandsSeparator ?? " "}
            max={max ?? Number.MAX_SAFE_INTEGER}
            min={min ?? Number.MIN_SAFE_INTEGER}
            value={value}
            name={name}
            id={name}
            variant={"outlined"}
            fullWidth
            error={hasError}
            helperText={errorMessage}
            onAccept={handleMaskInputChange}
            scale={scale}
            $hasFilterStyle={hasFilterStyle ?? false}
            $hasError={!!error}
            $isEmpty={!value}
            slotProps={{
              ...callerSlotProps,
              input: {
                endAdornment: inputEndAdornment,
                ...callerSlotProps?.input,
              },
            }}
            {...rest}
          />
        )}
        {unit && <StyledUnit className="bl-text-input__unit">{unit}</StyledUnit>}
      </div>
    </StyledInputWrapper>
  );
};
